import { createTheme, ThemeOptions } from "@mui/material";
import type { } from "@mui/x-data-grid/themeAugmentation";
import { green } from '@mui/material/colors';

const themeOption: ThemeOptions = {
	palette: {
		primary: {
			main: "#059669", // Light yellow-green logo color
			light: green[300], // Lighter shade of green
			dark: green[700], // Darker shade of green
			contrastText: '#ffffff', // White text for contrast
		},
		secondary: {
			main: green[500], // Standard green
			light: "#3c3c3c", // Light grey shade
			dark: "rgb(9, 109, 217)", // Blue shade for a darker contrast
			contrastText: '#ffffff', // White text for contrast
		},
		background: {
			default: "#fafafb"
		},
	},
	typography: {
		htmlFontSize: 16,
		fontFamily: `'Ubuntu'`,
		fontWeightLight: 300,
		fontWeightRegular: 400,
		fontWeightMedium: 500,
		fontWeightBold: 600,
		h1: {
			fontWeight: 600,
			fontSize: '2.375rem',
			lineHeight: 1.21
		},
		h2: {
			fontWeight: 600,
			fontSize: '1.875rem',
			lineHeight: 1.27
		},
		h3: {
			fontWeight: 600,
			fontSize: '1.5rem',
			lineHeight: 1.33
		},
		h4: {
			fontWeight: 600,
			fontSize: '1.25rem',
			lineHeight: 1.4
		},
		h5: {
			fontWeight: 600,
			fontSize: '1rem',
			lineHeight: 1.5
		},
		h6: {
			fontWeight: 400,
			fontSize: '0.875rem',
			lineHeight: 1.57
		},
		caption: {
			fontWeight: 400,
			fontSize: '0.75rem',
			lineHeight: 1.66
		},
		body1: {
			fontSize: '0.875rem',
			lineHeight: 1.57
		},
		body2: {
			fontSize: '0.75rem',
			lineHeight: 1.66
		},
		subtitle1: {
			fontSize: '0.875rem',
			fontWeight: 600,
			lineHeight: 1.57
		},
		subtitle2: {
			fontSize: '0.75rem',
			fontWeight: 500,
			lineHeight: 1.66
		},
		overline: {
			lineHeight: 1.66
		},
		button: {
			textTransform: 'capitalize'
		}
	},
	components: {
		MuiDataGrid: {
			styleOverrides: {

				root: {
					border: 'none',
				},

				columnHeader: {
					backgroundColor: "rgb(255, 255, 255)",
					color: "rgb(38, 38, 38)"
				},
				columnHeadersInner: {
					backgroundColor: "rgb(255, 255, 255)",
					color: "white"
				},
				columnHeaderCheckbox: {
					color: "white"
				},
				cellCheckbox: {
					backgroundColor: "rgb(255, 255, 255)",
				},
				menuIconButton: {
					color: "white"
				},
				sortIcon: {
					color: "rgb(217, 217, 217)"
				},
				filterIcon: {
					color: "white"
				}
			}
		},
		MuiButton: {
			styleOverrides: {
				root: {
					// Base styles for all buttons
					textTransform: 'none',
					borderRadius: '8px',
					fontWeight: 500,
					letterSpacing: '0.3px',
					padding: '8px 16px',
					transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
					position: 'relative',
					overflow: 'hidden',
					
					// Hover state for all buttons
					"&:hover": {
						backgroundColor: "#059669",
						color: "#fff",
						boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
						transform: 'translateY(-1px)',
					},
					
					// Active/pressed state
					"&:active": {
						transform: 'translateY(0)',
						boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)',
					},
					
					// Disabled state
					"&.Mui-disabled": {
						opacity: 0.6,
						cursor: 'not-allowed',
					},
					
					// Focus visible state
					"&.Mui-focusVisible": {
						boxShadow: '0 0 0 3px rgba(5, 150, 105, 0.2)',
					},
				},
				
				// Contained variant specific styles
				contained: {
					backgroundColor: '#059669',
					color: '#fff',
					"&:hover": {
						backgroundColor: '#047857',
						boxShadow: '0 6px 16px rgba(5, 150, 105, 0.3)',
						transform: 'translateY(-2px)',
					},
				},
				
				// Outlined variant specific styles
				outlined: {
					borderWidth: '1.5px',
					'&.MuiButton-outlinedPrimary': {
						borderColor: '#059669',
						color: '#059669',
						"&:hover": {
							backgroundColor: '#059669',
							borderColor: '#059669',
							color: '#fff',
						},
					},
				},
				
				// Text variant specific styles
				text: {
					"&:hover": {
						backgroundColor: 'rgba(5, 150, 105, 0.08)',
						color: '#059669',
						transform: 'none',
						boxShadow: 'none',
					},
				},
				
				// Size variants
				sizeSmall: {
					padding: '6px 12px',
					fontSize: '0.875rem',
				},
				sizeLarge: {
					padding: '12px 24px',
					fontSize: '1rem',
				},
				
				// Icon animations
				startIcon: {
					transition: 'transform 0.2s ease',
					'*:hover > &': {
						transform: 'translateX(-2px)',
					},
				},
				endIcon: {
					transition: 'transform 0.2s ease',
					'*:hover > &': {
						transform: 'translateX(2px)',
					},
				},
			}
		},
		MuiCard: {
			styleOverrides: {
				root: {
					transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
					boxShadow: 'rgb(0 0 0 / 10%) 0rem 0.25rem 0.375rem -0.0625rem, rgb(0 0 0 / 6%) 0rem 0.125rem 0.25rem -0.0625rem',
					borderRadius: '8px',
					// border: '1px solid rgb(230, 235, 241)',
				}
			}
		},
		MuiCardContent: {
			styleOverrides: {
				root: {
					padding: '16px 0px'
				}
			}
		},
		MuiToolbar: {
			styleOverrides: {
				root: {
					backgroundColor: 'white',
					boxShadow: 'rgb(0 0 0 / 10%) 0rem 0.25rem 0.375rem -0.0625rem, rgb(0 0 0 / 6%) 0rem 0.125rem 0.25rem -0.0625rem',
				}
			}
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					border: '1px solid rgb(230, 235, 241)',
					borderRadius: '8px'
				}
			}
		},
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					backgroundColor: '#f8fffe',
					borderBottom: '1px solid #d1fae5',
					padding: '12px 24px',
					margin: '0',
					minHeight: 'auto',
					'.MuiBox-root': {
						marginTop: '0px'
					},
					'& .MuiTypography-h5': {
						color: '#065f46',
						fontWeight: 600,
						fontSize: '1.125rem',
						letterSpacing: '0.2px',
						lineHeight: 1.4,
					},
					'& .MuiTypography-body2': {
						fontSize: '0.8rem',
						color: '#6b7280',
					},
					'& .MuiIconButton-root': {
						color: '#6b7280',
						'&:hover': {
							backgroundColor: 'rgba(5, 150, 105, 0.08)',
							color: '#059669',
						},
					},
				}
			}
		},
		MuiDialogActions: {
			styleOverrides: {
				root: {
					backgroundColor: '#fafafb !important',
					borderTop: '1px solid #e5e7eb',
					padding: '16px 24px !important',
					marginBottom: '0px !important',
					minHeight: '80px !important',
					height: 'auto !important',
					gap: '12px',
					display: 'flex',
					alignItems: 'center',
					'& .MuiGrid-container': {
						width: '100%',
						margin: '0 !important',
						paddingTop: '0 !important',
					},
					'& .MuiButton-root': {
						minWidth: '120px',
						height: '40px',
						borderRadius: '8px',
						fontWeight: 500,
						textTransform: 'none',
						fontSize: '14px',
						transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
					},
					'& .MuiButton-outlined': {
						borderColor: '#d1d5db',
						color: '#6b7280',
						backgroundColor: '#ffffff',
						'&:hover': {
							backgroundColor: '#f9fafb',
							borderColor: '#9ca3af',
							color: '#374151',
							boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
						},
					},
					'& .MuiButton-contained': {
						backgroundColor: '#059669',
						color: '#ffffff',
						boxShadow: '0 1px 3px rgba(5, 150, 105, 0.2)',
						'&:hover': {
							backgroundColor: '#047857',
							boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
							transform: 'translateY(-1px)',
						},
						'&:active': {
							transform: 'translateY(0)',
							boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)',
						},
					},
					'& .MuiLoadingButton-root': {
						backgroundColor: '#059669',
						color: '#ffffff',
						'&:hover': {
							backgroundColor: '#047857',
						},
						'&.Mui-disabled': {
							backgroundColor: '#9ca3af',
							color: '#ffffff',
						},
					},
				}
			}
		},
		MuiDrawer: {
			styleOverrides: {
				root: {
					'.MuiPaper-root': {
						boxShadow: 'rgb(0 0 0 / 10%) 0rem 0.25rem 0.375rem -0.0625rem, rgb(0 0 0 / 6%) 0rem 0.125rem 0.25rem -0.0625rem',
						// '@media (min-width: 1536px)': { padding: '40px 0 40px 40px' },
						// '@media (min-width: 900px)': { padding: '32px 0 32px 32px' },
						// '@media (min-width: 600px)': { padding: '24px 0 24px 24px' },
						// '@media (min-width: 0px)': { padding: '16px 0 16px 16px' },
					}
				}
			}
		},
        MuiAvatar: {
			
			styleOverrides: {
					img: {
					  objectFit: 'contain',
					  maxHeight: '100%',
					  maxWidth: '100%',
					},
				  },
				},
		MuiSvgIcon:{
			styleOverrides:{ 
				root: {
					'transition': 'all .3s ease 0s',
					'&[data-testid="ModeEditIcon"]:hover': {
						opacity: '.8'
					},
					'&[data-testid="ModeEditIcon"]': {
						cursor: 'pointer',
					}
				}
			}
		}
	}
}

const magnusTheme = createTheme(themeOption);

export default magnusTheme;
